// src/lib/TokenManager.ts
/**
 * Centralized Token Lifecycle Manager
 *
 * This module manages JWT access tokens and refresh tokens with the following capabilities:
 * - Automatic token refresh when access tokens expire
 * - Single-flight refresh pattern to prevent concurrent refresh requests
 * - Secure storage of tokens in localStorage with error handling
 * - Integration with axios interceptors for seamless API authentication
 *
 * Token Flow:
 * 1. Initial login: Store both access_token and refresh_token
 * 2. API requests: Automatically inject valid access token via getValidAccessToken()
 * 3. Token expiry: Detect expired access token and refresh using refresh_token
 * 4. Token rotation: Backend provides new access_token AND refresh_token on refresh
 * 5. Failure handling: Clear all tokens and redirect to login on refresh failure
 *
 * Security Features:
 * - Token rotation: Each refresh invalidates the previous refresh_token
 * - Single-flight: Only one refresh request at a time to prevent race conditions
 * - Isolated axios client: Uses separate client to avoid interceptor recursion
 * - Safe storage: Gracefully handles localStorage errors in SSR/test environments
 */

import axios, { AxiosInstance } from 'axios';
import { safeGetItem, safeRemoveItem, safeSetItem } from '@/utils/storage/storageUtils.ts';
import { ACCESS_TOKEN_EXP_KEY, ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY } from '@/utils/storage/constants.ts';
import { logTokenRefresh, logTokenRefreshSuccess, logTokenRefreshFailure, logTokenClear } from '@/utils/auth/logging';

const API_URL = import.meta.env.VITE_API_URL;

export class TokenManager {
	private accessToken: string | null;
	private refreshToken: string | null;
	private accessExp: number | null; // ms timestamp
	private refreshing: Promise<string | null> | null = null;
	// Bare axios instance without global interceptors to call refresh endpoint
	private refreshClient: AxiosInstance;
	// Subscription pattern for React component updates
	private listeners: Set<() => void> = new Set();

	constructor() {
		this.accessToken = safeGetItem(ACCESS_TOKEN_KEY);
		this.refreshToken = safeGetItem(REFRESH_TOKEN_KEY);
		this.accessExp = this.num(ACCESS_TOKEN_EXP_KEY);

		// Provide fallback baseURL for test environments where VITE_API_URL is undefined
		const baseURL = API_URL || 'http://localhost:8000';
		this.refreshClient = axios.create({ baseURL, timeout: 60_000 });
	}

	private num(key: string) {
		const v = safeGetItem(key);
		return v ? Number(v) : null;
	}

	private decodeExp(jwt: string): number | null {
		try {
			const [, payload] = jwt.split('.');
			const { exp } = JSON.parse(atob(payload));
			return exp ? exp * 1000 : null; // convert to ms
		} catch {
			return null;
		}
	}

	/**
	 * Sets both access and refresh tokens with automatic expiry calculation
	 * Stores tokens securely in localStorage using safe storage utilities
	 */
	setTokens({ accessToken, refreshToken }: { accessToken: string; refreshToken?: string }) {
		this.accessToken = accessToken;
		const exp = this.decodeExp(accessToken);
		this.accessExp = exp;
		safeSetItem(ACCESS_TOKEN_KEY, accessToken);

		if (refreshToken) {
			this.refreshToken = refreshToken;
			safeSetItem(REFRESH_TOKEN_KEY, refreshToken);
		}
		if (exp) {
			safeSetItem(ACCESS_TOKEN_EXP_KEY, String(exp));
		} else {
			safeRemoveItem(ACCESS_TOKEN_EXP_KEY);
		}

		// Notify React components of token change
		this.notifyListeners();
	}

	/**
	 * Subscribe to token changes for React component updates
	 * Returns an unsubscribe function
	 */
	subscribe(listener: () => void): () => void {
		this.listeners.add(listener);
		return () => this.listeners.delete(listener);
	}

	/**
	 * Notify all subscribers of token changes
	 */
	private notifyListeners() {
		this.listeners.forEach((listener) => listener());
	}

	/**
	 * Get current token without validation (for display purposes)
	 */
	getCurrentToken(): string | null {
		return this.accessToken;
	}

	/**
	 * Get current token expiry timestamp
	 */
	getCurrentExpiry(): number | null {
		return this.accessExp;
	}

	/**
	 * Check if we have a valid (non-expired) token
	 */
	hasValidToken(): boolean {
		return this.isAccessValid();
	}

	/**
	 * Check if token refresh is in progress
	 */
	isRefreshing(): boolean {
		return this.refreshing !== null;
	}

	/**
	 * Clears all token-related data from memory and localStorage
	 * Used during logout or when tokens become invalid
	 */
	clear() {
		logTokenClear('logout');
		this.accessToken = null;
		this.refreshToken = null;
		this.accessExp = null;
		safeRemoveItem(REFRESH_TOKEN_KEY);
		safeRemoveItem(ACCESS_TOKEN_EXP_KEY);
		safeRemoveItem(ACCESS_TOKEN_KEY);

		// Notify React components of token clear
		this.notifyListeners();
	}

	private isAccessValid(): boolean {
		if (!this.accessToken) return false;
		if (!this.accessExp) return true; // unknown exp, try using it
		return Date.now() < this.accessExp;
	}

	async getValidAccessToken(): Promise<string | undefined> {
		if (this.isAccessValid()) return this.accessToken!;
		logTokenRefresh('expired');
		const token = await this.refreshWithLock();
		return token ?? undefined;
	}

	private async refreshWithLock(): Promise<string | null> {
		if (!this.refreshToken) return null;
		if (!this.refreshing) {
			this.refreshing = this.refreshTokens().finally(() => {
				this.refreshing = null;
			});
		}
		return this.refreshing;
	}

	private async refreshTokens(): Promise<string | null> {
		try {
			const res = await this.refreshClient.post(
				'/token/refresh',
				{
					refresh_token: this.refreshToken,
				},
				{
					headers: { 'Content-Type': 'application/json' },
				},
			);

			if (res.status !== 200) {
				logTokenRefreshFailure(`HTTP ${res.status}`);
				return null;
			}

			const data = res.data as {
				access_token: string;
				refresh_token: string;
				token_type: 'bearer';
			};

			this.setTokens({ accessToken: data.access_token, refreshToken: data.refresh_token });
			logTokenRefreshSuccess(!!data.refresh_token);
			return data.access_token;
		} catch (error) {
			logTokenRefreshFailure(error instanceof Error ? error.message : 'Unknown error');
			return null;
		}
	}
}

export const tokenManager = new TokenManager();
